import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CaretLeftIcon, TrashIcon, CopyIcon, CheckIcon } from '@phosphor-icons/react';

import Sidebar from 'components/Sidebar';
import MobileHeader from 'components/MobileHeader';
import SearchOverlay from 'components/SearchOverlay';
import Container from 'components/Container';
import LoadingOverlay from 'components/LoadingOverlay';
import Button from 'components/Button';
import Popup from 'components/Popup';
import TextInput from 'components/Form/TextInput';

import { getAuthtokens, createApiKey, deleteAuthtoken } from 'api/authtokens';
import { getUsers } from 'api/users';
import { useFeedback } from 'contexts/feedback';

export default function AuthTokens() {
    const [sidebar, setSidebar] = useState(false);
    const [search, setSearch] = useState(false);
    const [createPopup, setCreatePopup] = useState(false);
    const [tokenPopup, setTokenPopup] = useState(false);
    const [deletePopup, setDeletePopup] = useState(null);
    const [createdToken, setCreatedToken] = useState('');
    const [copied, setCopied] = useState(false);

    const cache = useQueryClient();
    const { setSuccess, setError } = useFeedback();

    const { data: tokens, isLoading } = useQuery({
        queryKey: ['authtokens'],
        queryFn: async () => await getAuthtokens(),
    });

    const { data: users } = useQuery({
        queryKey: ['users'],
        queryFn: async () => await getUsers({}),
    });

    const { mutate: create, status: createStatus } = useMutation({
        mutationKey: ['createApiKey'],
        mutationFn: createApiKey,
        onSuccess: (data) => {
            cache.invalidateQueries(['authtokens']);
            setCreatePopup(false);
            setCreatedToken(data.token);
            setTokenPopup(true);
        },
        onError: () => {
            setError('Errore durante la creazione del token');
        },
    });

    const { mutate: remove, status: deleteStatus } = useMutation({
        mutationKey: ['deleteAuthtoken'],
        mutationFn: deleteAuthtoken,
        onSuccess: () => {
            cache.invalidateQueries(['authtokens']);
            setDeletePopup(null);
            setSuccess('Token eliminato');
        },
        onError: () => {
            setError("Errore durante l'eliminazione del token");
        },
    });

    function onCreateHandler(e) {
        e.preventDefault();
        const data = {
            name: e.target.name.value,
            user: e.target.user.value,
        };
        const expiresAt = e.target.expiresAt?.value;
        if (expiresAt) {
            data.expiresAt = new Date(expiresAt).toISOString();
        }
        create({ data });
    }

    function onDeleteHandler() {
        if (deletePopup) {
            remove({ id: deletePopup._id });
        }
    }

    function copyToken() {
        navigator.clipboard.writeText(createdToken);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }

    function formatDate(date) {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    }

    return (
        <>
            <Sidebar visible={sidebar} setVisible={setSidebar} />
            <SearchOverlay visible={search} setVisible={setSearch} />
            <MobileHeader
                sidebar={sidebar}
                setSidebar={setSidebar}
                search={search}
                setSearch={setSearch}
            />
            <LoadingOverlay isLoading={isLoading} />

            <div className={`fixed top-0 w-full bottom-0 transition-all duration-300 ease-in-out lg:pl-[320px] ${sidebar ? 'left-[80vw]' : 'left-0'}`}>
                <Container>
                    {/* Header */}
                    <div className='flex gap-3 items-center pb-8 pt-20 md:pt-14'>
                        <div className='flex-1 flex items-center gap-3'>
                            <Link to="/settings">
                                <CaretLeftIcon size={26} className='hover:text-muted' />
                            </Link>
                            <p className='text-3xl'>Token API</p>
                        </div>
                        <Button onClick={() => setCreatePopup(true)}>Crea token</Button>
                    </div>

                    {/* Token list */}
                    <div className='border border-light rounded-sm'>
                        {/* Table header */}
                        <div className='flex items-center gap-4 p-4 border-b border-light bg-verylight'>
                            <div className='flex-1'>
                                <p className='text-xs uppercase font-medium text-muted'>Nome</p>
                            </div>
                            <div className='flex-1 hidden md:block'>
                                <p className='text-xs uppercase font-medium text-muted'>Creato</p>
                            </div>
                            <div className='flex-1 hidden md:block'>
                                <p className='text-xs uppercase font-medium text-muted'>Scadenza</p>
                            </div>
                            <div className='flex-1 hidden md:block'>
                                <p className='text-xs uppercase font-medium text-muted'>Ultimo utilizzo</p>
                            </div>
                            <div className='w-10'></div>
                        </div>

                        {tokens?.filter(t => t.type === 'api_key').length === 0 && (
                            <div className='p-4 text-center text-muted'>
                                <p>Nessun token API creato</p>
                            </div>
                        )}

                        {tokens?.filter(t => t.type === 'api_key').map((token) => (
                            <div key={token._id} className='border-b border-light last:border-0 flex items-center gap-4 p-4'>
                                <div className='flex-1'>
                                    <p className='font-semibold'>{token.name || 'Token senza nome'}</p>
                                    {token.user && (
                                        <p className='text-muted text-xs'>{token.user.firstname} {token.user.lastname}</p>
                                    )}
                                </div>
                                <div className='flex-1 hidden md:block'>
                                    <p className='text-sm text-muted'>{formatDate(token.createdAt)}</p>
                                </div>
                                <div className='flex-1 hidden md:block'>
                                    {token.expiresAt ? (
                                        <p className={`text-sm ${new Date(token.expiresAt) < new Date() ? 'text-red-500' : 'text-muted'}`}>
                                            {formatDate(token.expiresAt)}
                                        </p>
                                    ) : (
                                        <p className='text-sm text-muted'>Permanente</p>
                                    )}
                                </div>
                                <div className='flex-1 hidden md:block'>
                                    <p className='text-sm text-muted'>
                                        {token.lastUsedAt ? formatDate(token.lastUsedAt) : 'Mai utilizzato'}
                                    </p>
                                </div>
                                <div className='w-10 flex justify-end'>
                                    <div
                                        onClick={() => setDeletePopup(token)}
                                        className='cursor-pointer text-muted hover:text-red-500'
                                    >
                                        <TrashIcon size={18} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Container>
            </div>

            {/* Create Token Popup */}
            <Popup visible={createPopup} setVisible={setCreatePopup} title="Crea token API">
                <form onSubmit={onCreateHandler} className='flex flex-col gap-4'>
                    <TextInput
                        label="Nome"
                        name="name"
                        placeholder="Es. Integrazione CRM"
                    />
                    <div className='w-full'>
                        <label className='text-sm mb-1 block'>Utente</label>
                        <select
                            name="user"
                            required
                            className='w-full border border-light text-sm rounded-md focus:ring-0 focus:outline-none focus:border-black px-4 py-3 block'
                        >
                            <option value="">Seleziona utente</option>
                            {users?.map((u) => (
                                <option key={u._id} value={u._id}>
                                    {u.firstname} {u.lastname} ({u.email})
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className='w-full'>
                        <label className='text-sm mb-1 block'>Scadenza (opzionale)</label>
                        <input
                            type="date"
                            name="expiresAt"
                            className='w-full border border-light text-sm rounded-md focus:ring-0 focus:outline-none focus:border-black placeholder:text-muted px-4 py-3 block'
                        />
                    </div>
                    <div className='flex justify-end pt-2'>
                        <Button submit status={createStatus}>Crea token</Button>
                    </div>
                </form>
            </Popup>

            {/* Token Created Popup */}
            <Popup visible={tokenPopup} setVisible={setTokenPopup} title="Token creato">
                <div className='flex flex-col gap-4'>
                    <p className='text-sm text-red-500 font-medium'>
                        Copia questo token adesso. Non sarà più visibile dopo aver chiuso questa finestra.
                    </p>
                    <div className='flex items-center gap-2 bg-verylight border border-light rounded-md p-3'>
                        <code className='flex-1 text-xs break-all select-all'>{createdToken}</code>
                        <div onClick={copyToken} className='cursor-pointer text-muted hover:text-black'>
                            {copied ? <CheckIcon size={18} /> : <CopyIcon size={18} />}
                        </div>
                    </div>
                    <div className='flex justify-end pt-2'>
                        <Button onClick={() => { setTokenPopup(false); setCreatedToken(''); setCopied(false); }}>
                            Ho copiato il token
                        </Button>
                    </div>
                </div>
            </Popup>

            {/* Delete Confirmation Popup */}
            <Popup visible={!!deletePopup} setVisible={() => setDeletePopup(null)} title="Elimina token">
                <div>
                    <p className='text-sm'>
                        Sei sicuro di voler eliminare il token <strong>{deletePopup?.name}</strong>? I sistemi esterni che lo utilizzano perderanno l&apos;accesso.
                    </p>
                    <div className='flex justify-between gap-4 mt-6'>
                        <div onClick={() => setDeletePopup(null)} className='cursor-pointer px-4 py-3 hover:bg-verylight rounded-sm'>
                            Annulla
                        </div>
                        <Button onClick={onDeleteHandler} status={deleteStatus}>Elimina</Button>
                    </div>
                </div>
            </Popup>
        </>
    );
}
